'use client'

import { useState, useCallback, useRef, useTransition } from 'react'
import { useRouter } from 'next/navigation'
import dynamic from 'next/dynamic'
import type { FilterValues } from '@/components/listings/FilterPanel'

const FilterPanel = dynamic(
  () => import('@/components/listings/FilterPanel').then((mod) => ({ default: mod.FilterPanel })),
  { ssr: false },
)

const EMPTY_FILTERS: FilterValues = {
  type: '',
  minPrice: '',
  maxPrice: '',
  bed: '',
  bath: '',
  car: '',
  surroundingSuburbs: false,
  vacancy: '',
  radius: '',
  offMarket: false,
}
import { SuburbSearch } from '@/components/listings/SuburbSearch'
import { AddressSearch } from './AddressSearch'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

type Tab = 'sell' | 'buy' | 'manage' | 'rent'

const tabs: { key: Tab; label: string }[] = [
  { key: 'sell', label: 'Sell' },
  { key: 'buy', label: 'Buy' },
  { key: 'manage', label: 'Manage' },
  { key: 'rent', label: 'Rent' },
]

export function HeroSearch() {
  const router = useRouter()
  // Steve 2026-08-19 — the CTA animates (Button `loading` spinner, same as the
  // buy page Search) while the navigation transition is in flight.
  const [isPending, startTransition] = useTransition()
  const [activeTab, setActiveTab] = useState<Tab>('sell')
  const [buySearch, setBuySearch] = useState('')
  const [rentSearch, setRentSearch] = useState('')
  const [sellAddress, setSellAddress] = useState('')
  const [manageAddress, setManageAddress] = useState('')

  const buySearchRef = useRef(buySearch)
  buySearchRef.current = buySearch
  const rentSearchRef = useRef(rentSearch)
  rentSearchRef.current = rentSearch

  const handleBuySubmit = useCallback(() => {
    startTransition(() => {
      router.push(
        buySearchRef.current.trim()
          ? `/buy?search=${encodeURIComponent(buySearchRef.current.trim())}`
          : '/buy',
      )
    })
  }, [router])

  const handleRentSubmit = useCallback(() => {
    startTransition(() => {
      router.push(
        rentSearchRef.current.trim()
          ? `/rent?search=${encodeURIComponent(rentSearchRef.current.trim())}`
          : '/rent',
      )
    })
  }, [router])

  // With an address already supplied, step 1 (the address question) is answered —
  // skip straight to step 2, matching the sub-footer appraisal bar (Steve 2026-08-22).
  // Without one, start at step 1 so they can enter it there.
  const handleSellSubmit = () => {
    startTransition(() => {
      if (sellAddress.trim()) {
        router.push(`/appraisal/step-2?address=${encodeURIComponent(sellAddress.trim())}`)
      } else {
        router.push('/appraisal')
      }
    })
  }

  const handleManageSubmit = () => {
    startTransition(() => {
      if (manageAddress.trim()) {
        router.push(
          `/appraisal/step-2?address=${encodeURIComponent(manageAddress.trim())}&type=manage`,
        )
      } else {
        router.push('/appraisal')
      }
    })
  }

  const isAppraisalTab = activeTab === 'sell' || activeTab === 'manage'
  const buttonLabel = isAppraisalTab ? 'Start Appraisal' : 'Search'

  // Asana 005 (Figma 4054:31086) — Buy/Rent carry a compact filter button that
  // opens the filter panel HERE on the home page (Steve 2026-08-19); applying
  // it navigates to the listing page with the chosen filters in the URL.
  const [filtersOpen, setFiltersOpen] = useState(false)

  const handleApplyFilters = (filters: FilterValues) => {
    const base = activeTab === 'rent' ? '/rent' : '/buy'
    const term = activeTab === 'rent' ? rentSearchRef.current.trim() : buySearchRef.current.trim()
    const params = new URLSearchParams()
    if (term) params.set('search', term)
    if (filters.type) params.set('type', filters.type)
    if (filters.minPrice) params.set('minPrice', filters.minPrice)
    if (filters.maxPrice) params.set('maxPrice', filters.maxPrice)
    if (filters.bed) params.set('bed', filters.bed)
    if (filters.bath) params.set('bath', filters.bath)
    if (filters.car) params.set('car', filters.car)
    if (filters.surroundingSuburbs) params.set('surroundingSuburbs', 'true')
    if (filters.offMarket) params.set('offMarket', 'true')
    const qs = params.toString()
    startTransition(() => router.push(qs ? `${base}?${qs}` : base))
  }

  const handleButtonClick = () => {
    switch (activeTab) {
      case 'sell':
        return handleSellSubmit()
      case 'buy':
        return handleBuySubmit()
      case 'manage':
        return handleManageSubmit()
      case 'rent':
        return handleRentSubmit()
    }
  }

  return (
    // Figma Frame 766 (4022:82010) — a FLUID half of the content column: (100% - gap-16)/2,
    // which is 704px at the 1600 desktop artboard but 624 at 1440. The parent in
    // HighImpact/index.tsx supplies that half-width track via `lg:grid-cols-2 gap-x-16`,
    // so no max-width belongs here — a hardcoded 704px cap would bind above ~1728px viewport
    // and make the cluster narrower than the design. gap/gap-4 16px between tabs and input row.
    <Tabs
      className="flex flex-col gap-4"
      onValueChange={(value) => setActiveTab(value as Tab)}
      value={activeTab}
    >
      {/* Tab pill row — canonical Tabs (Figma tab 2198:41383 / tabs 2198:41426, instance
          4022:82011): muted track #e0dfe0, p-0 gap-0 radius sm; each tab h-6 px-2 py-1,
          Lato 400 14/16, radius sm; active = accent #8cc640 + shadow-sm. The component
          does not force caps, so the caps that instance 4022:82011 shows ("SELL", "BUY",
          "MANAGE", "RENT") are added here. Previously hand-rolled: it hardcoded the
          geometry, shipped no focus ring, no Figma hover swap, and no roving-tabindex /
          arrow-key navigation. */}
      <TabsList aria-label="Property search type">
        {tabs.map((tab) => (
          <TabsTrigger className="uppercase" key={tab.key} value={tab.key}>
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>

      {/* Input row — Figma Frame 577: gap/gap-1 4px. Desktop (4022:82012) is a row
          (trigger 547 + button 153); tablet 4022:94494 and mobile 4022:100054 stack the
          button under a full-width trigger. Each TabsContent replaces the old
          conditionally-rendered wrapper div one-for-one (inactive panels unmount, exactly
          as before) and now carries the aria-labelledby/id pairing the bare
          role="tabpanel" div was missing. */}
      <div className="flex flex-col lg:flex-row gap-1 items-start w-full">
        <TabsContent className="w-full min-w-0 lg:flex-1" value="sell">
          <AddressSearch
            value={sellAddress}
            onChange={setSellAddress}
            placeholder="Type your address"
          />
        </TabsContent>
        <TabsContent className="w-full min-w-0 lg:flex-1" value="buy">
          <SuburbSearch
            value={buySearch}
            onChange={setBuySearch}
            onSubmit={handleBuySubmit}
            placeholder="Search Suburb or Postcode"
            variant="hero"
          />
        </TabsContent>
        <TabsContent className="w-full min-w-0 lg:flex-1" value="manage">
          <AddressSearch
            value={manageAddress}
            onChange={setManageAddress}
            placeholder="Type your address"
          />
        </TabsContent>
        <TabsContent className="w-full min-w-0 lg:flex-1" value="rent">
          <SuburbSearch
            value={rentSearch}
            onChange={setRentSearch}
            onSubmit={handleRentSubmit}
            placeholder="Search Suburb or Postcode"
            variant="hero"
          />
        </TabsContent>

        {/* Asana 005 — Buy/Rent only: square outline filter button between the
            field and Search (Figma 4054:31086), opens the listing page's panel. */}
        {!isAppraisalTab && (
          <>
            <Button
              onClick={() => setFiltersOpen(true)}
              variant="outline"
              size="icon"
              className="hidden shrink-0 lg:inline-flex"
              aria-label={`Open ${activeTab === 'rent' ? 'rent' : 'buy'} filters`}
            >
              {/* Asana 026 — same 3-line list-filter glyph as the listing pages (Figma 4081:130713) */}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M3 6h18" />
                <path d="M7 12h10" />
                <path d="M10 18h4" />
              </svg>
            </Button>
            <FilterPanel
              open={filtersOpen}
              onClose={() => setFiltersOpen(false)}
              onApply={handleApplyFilters}
              onClear={() => setFiltersOpen(false)}
              initial={EMPTY_FILTERS}
              vertical={activeTab === 'rent' ? 'rent' : 'sale'}
            />
          </>
        )}

        {/* Primary button — Figma button 4035:147451 = type=primary, size=Regular, which the
            canonical Button expresses as variant="default" size="default" (h-36, px-24, py-8,
            Lato 400 16/24, radius sm). Previously hand-rolled: it hardcoded the geometry and
            used `btn-underline` + `hover:bg-accent/85`, neither of which Figma defines — the
            Figma hover is a swap to theme/secondary. */}
        <Button
          onClick={handleButtonClick}
          loading={isPending}
          className="shrink-0 whitespace-nowrap"
        >
          {buttonLabel}
        </Button>
      </div>
    </Tabs>
  )
}
